import type { DocumentV1 } from '@alpod-ai-writer/backend';
import { LinkPlugin } from '@alpod-ai-writer/plate-plugins/link/react';
import { NodeIdPlugin } from '@alpod-ai-writer/plate-plugins/node-id';
import { SuggestionPlugin } from '@alpod-ai-writer/plate-plugins/suggestion/react';
import type { CursorData } from '@alpod-ai-writer/shared';
import { withProps } from '@udecode/cn';
import { type Value } from '@udecode/plate';
import { ParagraphPlugin, PlateElement, PlateLeaf, usePlateEditor } from '@udecode/plate/react';
import { BasicMarksPlugin, BoldPlugin, ItalicPlugin, SubscriptPlugin, SuperscriptPlugin, UnderlinePlugin } from '@udecode/plate-basic-marks/react';
import { CommentsPlugin } from '@udecode/plate-comments/react';
import { DocxPlugin } from '@udecode/plate-docx';
import { JuicePlugin } from '@udecode/plate-juice';
import { MarkdownPlugin } from '@udecode/plate-markdown';
import { BlockSelectionPlugin } from '@udecode/plate-selection/react';

import { BlockDiscussion } from '../components/common/blockDiscussion.component';
import { BlockSelection } from '../components/common/blockSelection.component';
import { CommentLeaf } from '../components/elements/commentLeaf.component';
import { LinkElementStatic } from '../components/elements/linkElementStatic.component';
import { SuggestionLeaf } from '../components/elements/suggestionLeaf.component';
import { SuggestionBelowNodes } from '../components/elements/suggestionLineBreak.component';
import { commentsPlugin } from '../plugins/commentsPlugin';
import { suggestionPlugin } from '../plugins/suggestionPlugin';

export const basicNodesPlugins = [
    BasicMarksPlugin,
    LinkPlugin,
    MarkdownPlugin.configure({ options: { indentList: true } }),
    JuicePlugin,
    DocxPlugin
] as const;

export const InlineEditorStaticComponents = {
    [BoldPlugin.key]: withProps(PlateLeaf, { as: 'strong' }),
    [ItalicPlugin.key]: withProps(PlateLeaf, { as: 'em' }),
    [UnderlinePlugin.key]: withProps(PlateLeaf, { as: 'u' }),
    [SubscriptPlugin.key]: withProps(PlateLeaf, { as: 'sub' }),
    [SuperscriptPlugin.key]: withProps(PlateLeaf, { as: 'sup' }),
    [ParagraphPlugin.key]: withProps(PlateElement, {
        as: 'p',
        className: 'leading-relaxed',
    }),
    [LinkPlugin.key]: LinkElementStatic,
    [SuggestionPlugin.key]: SuggestionLeaf,
    [CommentsPlugin.key]: CommentLeaf,
};

export interface InlineEditorStaticOptions {
    document: DocumentV1.Record;
    cursorData: CursorData;
    value?: Value;
}

export function useInlineEditorStatic(opts: InlineEditorStaticOptions) {
    const editor = usePlateEditor({
        value: opts.value,
        plugins: [
            ...basicNodesPlugins,
            suggestionPlugin.configure({
                options: {
                    currentUserId: opts.cursorData.identityId
                },
                render: { belowNodes: SuggestionBelowNodes as any },
            }),
            commentsPlugin.configure({
                render: { aboveNodes: BlockDiscussion as any }
            }),
            BlockSelectionPlugin.configure({
                render: {
                    belowRootNodes: (props) => {
                        if (!props.className?.includes('slate-selectable')) return null;
                        return <BlockSelection />;
                    },
                },
            }),
            NodeIdPlugin
        ],
        override: {
            components: InlineEditorStaticComponents
        }
    }, []);

    return editor;
}
